import { useRef, useState } from 'react';
import classNames from 'classnames';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { CloseIcon, NotificationIcon } from '../../assets/icons';
import { BookingSelectors } from '../../redux/reducers/bookingSlice';
import { IntegrationSelectors } from '../../redux/reducers/integrationSlice';
import { BookingFormType } from '../../redux/types/bookingTypes';
import { ModalWindowTypes } from '../../utils/@globalTypes';
import { setModalWindowData } from '../../redux/reducers/pageSlice';
import { useClickOutside } from '../../utils/functions';
import { getBookingStatusName } from '../Booking/functions';
import { getUnviewedBookingNotifications } from './notificationModel';

import styles from './NotificationSign.module.scss';

type NoteProp = {
  color?: string;
};

const NotificationSign = ({ color }: NoteProp) => {
  const dispatch = useDispatch();
  const objects = useSelector(BookingSelectors.getChessObjects);
  const bookingArray = useSelector(BookingSelectors.getBookingList);
  const externalBookings = useSelector(IntegrationSelectors.getExternalBookings);
  const connections = useSelector(IntegrationSelectors.getConnections);
  const filteredList = getUnviewedBookingNotifications(bookingArray, externalBookings);
  const countNotViewed = filteredList.length;
  const displayCount =
    countNotViewed && countNotViewed > 99
      ? '99+'
      : countNotViewed && countNotViewed > 0
        ? countNotViewed
        : undefined;

  const [isOpenList, setIsOpenList] = useState<boolean>(false);
  const openList = () => {
    setIsOpenList(!isOpenList);
  };
  const listRef = useRef(null);
  const btnRef = useRef(null);

  useClickOutside(listRef, btnRef, () => {
    setIsOpenList(false);
  });
  const countTitle = countNotViewed
    ? countNotViewed % 10 === 1 && countNotViewed % 100 !== 11
      ? 'новое уведомление'
      : countNotViewed % 10 >= 2 && countNotViewed % 10 <= 4 &&
          (countNotViewed % 100 < 12 || countNotViewed % 100 > 14)
        ? 'новых уведомления'
        : 'новых уведомлений'
    : '';

  return (
    <div className={styles.wrap} ref={listRef}>
      <div
        className={styles.container}
        onClick={openList}
        ref={btnRef}
      >
        <div>
          <NotificationIcon fill={color} />
        </div>
        {!!countNotViewed && <div className={styles.count}>{displayCount}</div>}
      </div>
      {isOpenList && countNotViewed > 0 && (
        <div className={styles.popup_container}>
          <div className={styles.popup_header}>
            <div>
              У вас {countNotViewed}&ensp;{countTitle}
            </div>
            <div ref={btnRef} onClick={() => setIsOpenList(false)}>
              <CloseIcon width="20" height="20" fill="#0A3A86" />
            </div>
          </div>
          <div className={styles.popup_list}>
            {filteredList.map((notification) => {
              const item = notification.booking;
              let objectName: string = '';
              objects &&
                objects.map((obj) => {
                  if (obj.rental_objects.length > 0) {
                    const ind = obj.rental_objects.find(
                      (rent) => rent.id === item.rental_object,
                    );
                    if (ind) objectName = ind.name;
                  }
                });
              const openNotification = () => {
                if (notification.type === 'internal') {
                  dispatch(setModalWindowData({
                    type: ModalWindowTypes.SHOW_RESERVATION,
                    data: { type: BookingFormType.view, booking: notification.booking },
                  }));
                } else {
                  dispatch(setModalWindowData({
                    type: ModalWindowTypes.SHOW_EXTERNAL_RESERVATION,
                    data: { id: notification.booking.id },
                  }));
                }
                setIsOpenList(false);
              };

              if (notification.type === 'external') {
                const externalStatus = notification.booking.booking_status === 'awt'
                  ? 'Ожидает подтверждения'
                  : 'Подтверждена';
                const source = notification.booking.source_type === 'ical'
                  ? 'iCalendar'
                  : 'Менеджер каналов';
                const connection = connections.find(
                  (item) => item.id === notification.booking.source_connection,
                );
                return (
                  <div
                    className={styles.item_wrap}
                    key={`external-${notification.booking.id}`}
                    role="button"
                    tabIndex={0}
                    onClick={openNotification}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openNotification();
                      }
                    }}
                  >
                    <div className={styles.item_name}>{objectName}</div>
                    <div
                      className={classNames(
                        styles.item_status,
                        styles[
                          notification.booking.sync_status === 'cancelled'
                            ? 'cancelled'
                            : notification.booking.booking_status === 'awt'
                              ? 'await'
                              : 'full_paid'
                        ],
                      )}
                    >
                      {notification.booking.sync_status === 'cancelled' ? 'Отменена' : externalStatus}
                    </div>
                    <div className={styles.item_main}>
                      Внешняя бронь · {source}{connection ? ` · ${connection.name}` : ''}, {notification.booking.last_name} {notification.booking.first_name},&ensp;
                      {format(notification.booking.check_in_date, 'dd.MM.yyyy')}-
                      {format(notification.booking.check_out_date, 'dd.MM.yyyy')}
                    </div>
                    <div className={styles.item_create}>
                      {format(notification.booking.created_at, 'dd.MM.yyyy')} в{' '}
                      {format(notification.booking.created_at, 'H:m')}
                    </div>
                  </div>
                );
              }

              const internalBooking = notification.booking;
              const statusName = getBookingStatusName(internalBooking.status);
              const fullPrice = (
                Number(internalBooking.accommodation_price) +
                Number(internalBooking.total_board_price) +
                Number(internalBooking.total_animal_price)
              ).toFixed(2).toString();
              const bookingClassname =
                internalBooking.status === 'new'
                  ? 'new'
                  : internalBooking.status === 'awt'
                    ? 'await'
                    : (+internalBooking.accommodation_price + +internalBooking.total_board_price + +internalBooking.total_animal_price) === +internalBooking.paid
                      ? 'full_paid'
                      : +internalBooking.paid > 0
                        ? 'part_paid'
                        : 'not_paid';
              return (
                <div
                  className={styles.item_wrap}
                  key={`internal-${internalBooking.id}`}
                  role="button"
                  tabIndex={0}
                  onClick={openNotification}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openNotification();
                    }
                  }}
                >
                  <div className={styles.item_name}>{objectName}</div>
                  <div
                    className={classNames(
                      styles.item_status,
                      styles[bookingClassname],
                    )}
                  >
                    {statusName}
                  </div>
                  <div className={styles.item_main}>
                    {internalBooking.last_name} {internalBooking.first_name} {internalBooking.second_name},
                    &ensp;
                    {format(internalBooking.check_in_date, 'dd.MM.yyyy')}-
                    {format(internalBooking.check_out_date, 'dd.MM.yyyy')},&ensp;
                    {fullPrice} BYN
                  </div>
                  <div className={styles.item_create}>
                    {format(internalBooking.created_at, 'dd.MM.yyyy')} в{' '}
                    {format(internalBooking.created_at, 'H:m')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationSign;
